import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Briefcase, Award } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const PILLARS = [
  { key: "trust", icon: ShieldCheck },
  { key: "expertise", icon: Briefcase },
  { key: "quality", icon: Award },
];

const PILLAR_COPY = {
  tr: {
    eyebrow: "REFERANSLARIMIZ",
    title: "Güven inşa eden kurumsal ilişkiler.",
    lead: "Uluslararası yatırımcılar, kurumsal firmalar ve kamu paydaşları için uçtan uca projeler yürütüyoruz. Gizlilik taahhüdümüz gereği, referanslarımızı talep üzerine paylaşıyoruz.",
    items: {
      trust: { title: "Güvene Dayalı İşbirliği", body: "Yatırımcı ilişkileri ve çok paydaşlı projelerde şeffaf raporlama ve hesap verebilirlik." },
      expertise: { title: "Sektörel Uzmanlık", body: "Yabancı danışmanlık, inşaat, ihracat, IT ve siber güvenlik alanlarında kıdemli kadro." },
      quality: { title: "Uluslararası Standart", body: "ISO uyumlu süreçler, denetimli teslim ve ölçülebilir iş sonuçları." },
    },
  },
  en: {
    eyebrow: "OUR REFERENCES",
    title: "Corporate relationships built on trust.",
    lead: "We deliver end-to-end engagements for international investors, corporate clients and public stakeholders. In line with our confidentiality commitments, references are shared upon request.",
    items: {
      trust: { title: "Trust-based Partnership", body: "Transparent reporting and accountability for investor relations and multi-stakeholder programs." },
      expertise: { title: "Sector Expertise", body: "Senior teams across foreign advisory, construction, export, IT and cybersecurity." },
      quality: { title: "International Standards", body: "ISO-aligned processes, audited delivery and measurable business outcomes." },
    },
  },
  ar: {
    eyebrow: "مراجعنا",
    title: "علاقات مؤسسية مبنية على الثقة.",
    lead: "ننفّذ مشاريع شاملة للمستثمرين الدوليين والعملاء من الشركات والجهات العامة. وفقًا لالتزاماتنا بالسرية، نشارك المراجع عند الطلب.",
    items: {
      trust: { title: "شراكة قائمة على الثقة", body: "تقارير شفافة ومساءلة في علاقات المستثمرين والبرامج متعددة الأطراف." },
      expertise: { title: "خبرة قطاعية", body: "فرق كبيرة في الاستشارات الأجنبية والإنشاءات والتصدير وتقنية المعلومات والأمن السيبراني." },
      quality: { title: "معايير دولية", body: "عمليات متوافقة مع ISO وتسليم مدقق ونتائج قابلة للقياس." },
    },
  },
  es: {
    eyebrow: "NUESTRAS REFERENCIAS",
    title: "Relaciones corporativas basadas en la confianza.",
    lead: "Ejecutamos proyectos integrales para inversores internacionales, empresas y entidades públicas. Por compromiso de confidencialidad, compartimos nuestras referencias bajo solicitud.",
    items: {
      trust: { title: "Alianza basada en confianza", body: "Reporte transparente y rendición de cuentas en relaciones con inversores y programas multilaterales." },
      expertise: { title: "Experiencia sectorial", body: "Equipos senior en consultoría internacional, construcción, exportación, TI y ciberseguridad." },
      quality: { title: "Estándares internacionales", body: "Procesos alineados a ISO, entrega auditada y resultados medibles." },
    },
  },
  ru: {
    eyebrow: "НАШИ РЕФЕРЕНСЫ",
    title: "Корпоративные отношения, построенные на доверии.",
    lead: "Мы ведём сквозные проекты для международных инвесторов, корпоративных клиентов и государственных партнёров. В соответствии с обязательствами по конфиденциальности, референсы предоставляются по запросу.",
    items: {
      trust: { title: "Партнёрство на основе доверия", body: "Прозрачная отчётность и подотчётность в отношениях с инвесторами и многосторонних программах." },
      expertise: { title: "Отраслевая экспертиза", body: "Опытные команды в консалтинге, строительстве, экспорте, IT и кибербезопасности." },
      quality: { title: "Международные стандарты", body: "Процессы по ISO, аудит поставки и измеримые бизнес-результаты." },
    },
  },
  de: {
    eyebrow: "UNSERE REFERENZEN",
    title: "Unternehmensbeziehungen auf Vertrauensbasis.",
    lead: "Wir führen End-to-End-Projekte für internationale Investoren, Unternehmenskunden und öffentliche Partner durch. Gemäß unserer Vertraulichkeitsverpflichtung teilen wir Referenzen auf Anfrage.",
    items: {
      trust: { title: "Vertrauensvolle Partnerschaft", body: "Transparentes Reporting und Rechenschaft bei Investor Relations und Multi-Stakeholder-Programmen." },
      expertise: { title: "Branchen-Expertise", body: "Erfahrene Teams in Auslandsberatung, Bau, Export, IT und Cybersicherheit." },
      quality: { title: "Internationale Standards", body: "ISO-konforme Prozesse, geprüfte Lieferung und messbare Geschäftsergebnisse." },
    },
  },
  fr: {
    eyebrow: "NOS RÉFÉRENCES",
    title: "Des relations corporate bâties sur la confiance.",
    lead: "Nous menons des projets de bout en bout pour des investisseurs internationaux, des clients corporate et des acteurs publics. Conformément à nos engagements de confidentialité, les références sont partagées sur demande.",
    items: {
      trust: { title: "Partenariat de confiance", body: "Reporting transparent et redevabilité pour les relations investisseurs et les programmes multipartenaires." },
      expertise: { title: "Expertise sectorielle", body: "Équipes senior en conseil international, construction, export, IT et cybersécurité." },
      quality: { title: "Standards internationaux", body: "Processus alignés ISO, livraisons auditées et résultats mesurables." },
    },
  },
};

const References = () => {
  const { locale } = useI18n();
  const r = PILLAR_COPY[locale] || PILLAR_COPY.en;

  return (
    <section
      id="references"
      data-testid="references-section"
      className="py-20 md:py-28 bg-[hsl(var(--tbs-cream-50))]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">
            {r.eyebrow}
          </div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl leading-[1.08] text-[hsl(var(--primary))]">
            {r.title}
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed">{r.lead}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            const copy = r.items[p.key];
            return (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                data-testid={`references-pillar-${p.key}`}
                className="card-lift relative bg-white border border-slate-200 rounded-2xl p-7"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-xl text-[hsl(var(--primary))]">{copy.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{copy.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default References;
